import { Database, X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { type DataBinding, DataBindingService } from "@/services/dataBindingService";
import { useEditorStore } from "@/store/editorStore";

interface DataBindingButtonProps {
  componentId: string;
  componentType: string;
  propertyPath: string;
  currentBinding?: DataBinding;
  onBindingChange: (binding: DataBinding | null) => void;
  isPartOfCollectionList?: boolean;
}

export const DataBindingButton: React.FC<DataBindingButtonProps> = ({
  componentId,
  componentType: _componentType,
  propertyPath,
  currentBinding,
  onBindingChange,
  isPartOfCollectionList = false,
}) => {
  const { toast } = useToast();
  const { project } = useEditorStore();
  const hideDataBindingUi = true;

  const [isOpen, setIsOpen] = useState(false);
  const [datasets, setDatasets] = useState<Array<Record<string, unknown>>>([]);
  const [selectedDataset, setSelectedDataset] = useState<string>("");
  const [fields, setFields] = useState<Array<Record<string, unknown>>>([]);
  const [selectedField, setSelectedField] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const loadDatasets = useCallback(async () => {
    if (!project?.id) return;

    try {
      setIsLoading(true);
      const availableDatasets = await DataBindingService.getAvailableDatasets(project.id);
      setDatasets(availableDatasets);
    } catch (error) {
      console.error("Error loading datasets:", error);
      toast({
        variant: "destructive",
        title: "Failed to load datasets",
        description: "Could not load available CMS datasets.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [project?.id, toast]);

  const loadFields = useCallback(
    async (datasetId: string) => {
      if (!project?.craftspace_id) return;

      try {
        const datasetFields = await DataBindingService.getDatasetFields(
          datasetId,
          project.craftspace_id,
        );
        setFields(datasetFields);
      } catch (error) {
        console.error("Error loading fields:", error);
        toast({
          variant: "destructive",
          title: "Failed to load fields",
          description: "Could not load dataset fields.",
        });
      }
    },
    [project?.craftspace_id, toast],
  );

  // Load available datasets when dialog opens
  useEffect(() => {
    if (isOpen && project?.id) {
      loadDatasets();
    }
  }, [isOpen, project?.id, loadDatasets]);

  // Load fields when dataset changes
  useEffect(() => {
    if (selectedDataset && project?.craftspace_id) {
      loadFields(selectedDataset);
    }
  }, [selectedDataset, project?.craftspace_id, loadFields]);

  // Initialize with current binding
  useEffect(() => {
    if (currentBinding) {
      setSelectedDataset(currentBinding.cmsDatasetId);
      setSelectedField(currentBinding.fieldName);
    }
  }, [currentBinding]);

  const handleCreateBinding = () => {
    if (!selectedDataset || !selectedField) {
      toast({
        variant: "destructive",
        title: "Incomplete selection",
        description: "Please select both a dataset and a field.",
      });
      return;
    }

    const field = fields.find((f) => f.name === selectedField);
    if (!field) return;

    // Check if the selected field is compatible
    if (!isFieldCompatible(field)) {
      toast({
        variant: "destructive",
        title: "Incompatible field type",
        description: `Field type "${field.fieldType}" is not compatible with this property.`,
      });
      return;
    }

    const binding = DataBindingService.createBinding(
      selectedDataset,
      selectedField,
      field.displayName,
      field.fieldType,
    );

    onBindingChange(binding);
    setIsOpen(false);

    toast({
      title: "Data binding created",
      description: `Bound to ${field.displayName} from dataset.`,
    });
  };

  const handleRemoveBinding = () => {
    onBindingChange(null);
    setSelectedDataset("");
    setSelectedField("");
    setIsOpen(false);

    toast({
      title: "Data binding removed",
      description: "Component is no longer bound to CMS data.",
    });
  };

  const getFieldTypeColor = (fieldType: string) => {
    switch (fieldType) {
      case "text":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "longtext":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "number":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "url":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case "date":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "switch":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  // Determine which field types are allowed for this property
  const getAllowedFieldTypes = (propertyPath: string): string[] => {
    // For image source, only allow URL fields
    if (propertyPath === "content.src") {
      return ["url"];
    }

    // For text content, allow all field types (text can display any data)
    if (propertyPath === "content.text") {
      return ["text", "longtext", "number", "url", "date", "switch"];
    }

    // For visibility, only allow boolean/switch fields
    if (propertyPath === "visible") {
      return ["switch"];
    }

    // For other properties, allow all field types
    return ["text", "longtext", "number", "url", "date", "switch"];
  };

  // Check if a field is compatible with the current property
  const isFieldCompatible = (field: Record<string, unknown>): boolean => {
    const allowedTypes = getAllowedFieldTypes(propertyPath);
    return allowedTypes.includes(field.fieldType);
  };

  // Hide binding button for visibility property when component is part of Collection
  if (isPartOfCollectionList && propertyPath === "visible") {
    return null;
  }
  if (hideDataBindingUi) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`absolute right-1 top-1 h-4 w-4 p-0 rounded-full ${
            currentBinding
              ? "bg-purple-600 hover:bg-purple-700 text-white"
              : "bg-purple-500 hover:bg-purple-600 text-white"
          }`}
          title={currentBinding ? `Bound to ${currentBinding.displayName}` : "Bind to CMS data"}
        >
          {currentBinding ? (
            <div className="h-1 w-1 bg-white rounded-full" />
          ) : (
            <Database className="h-2 w-2" />
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Database className="h-4 w-4 text-purple-400" />
            Data Binding
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Connect this field to CMS data for dynamic content
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          {/* Current binding info */}
          {currentBinding && (
            <div className="p-3 bg-purple-900/20 border border-purple-600/30 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-300 font-medium">Currently bound to:</p>
                  <p className="text-white text-sm">{currentBinding.displayName}</p>
                  <Badge className={`text-xs mt-1 ${getFieldTypeColor(currentBinding.fieldType)}`}>
                    {currentBinding.fieldType}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveBinding}
                  className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Dataset selection */}
          <div>
            <Label className="text-gray-300 text-sm">Select Dataset</Label>
            <Select value={selectedDataset} onValueChange={setSelectedDataset}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Choose a dataset..." />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {datasets
                  .filter((dataset) => dataset?.id && dataset.id.trim() !== "")
                  .map((dataset) => (
                    <SelectItem key={dataset.id} value={dataset.id} className="text-white">
                      <div className="flex items-center gap-2">
                        <Database className="h-3 w-3" />
                        <span>{dataset.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {dataset.rowCount} rows
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Field selection */}
          {selectedDataset && (
            <div>
              <Label className="text-gray-300 text-sm">Select Field</Label>
              {propertyPath === "content.src" && (
                <p className="text-xs text-gray-400 mt-1 mb-2">
                  Only URL fields are compatible with image sources
                </p>
              )}
              {propertyPath === "content.text" && (
                <p className="text-xs text-gray-400 mt-1 mb-2">
                  All field types are compatible with text content
                </p>
              )}
              {propertyPath === "visible" && (
                <p className="text-xs text-gray-400 mt-1 mb-2">
                  Only boolean/switch fields are compatible with visibility
                </p>
              )}
              <Select value={selectedField} onValueChange={setSelectedField}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Choose a field..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  {fields
                    .filter((field) => field?.name && field.name.trim() !== "")
                    .map((field) => {
                      const isCompatible = isFieldCompatible(field);
                      return (
                        <SelectItem
                          key={field.name}
                          value={field.name}
                          className={`text-white ${!isCompatible ? "opacity-50 cursor-not-allowed" : ""}`}
                          disabled={!isCompatible}
                        >
                          <div className="flex items-center gap-2">
                            <span className={!isCompatible ? "text-gray-400" : ""}>
                              {field.displayName || field.name}
                            </span>
                            <Badge
                              className={`text-xs ${getFieldTypeColor(field.fieldType)} ${!isCompatible ? "opacity-50" : ""}`}
                            >
                              {field.fieldType}
                            </Badge>
                            {!isCompatible && (
                              <span className="text-xs text-gray-500 ml-1">(incompatible)</span>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleCreateBinding}
              disabled={!selectedDataset || !selectedField || isLoading}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              {currentBinding ? "Update Binding" : "Create Binding"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
